import { useState, useEffect, useCallback } from 'react';
import { ResultPod } from '../mathEngine';
import MathRenderer from '../../../components/base/MathRenderer';

// ─── Mode System ──────────────────────────────────────────────────────────────

type ExplainMode = 'standard' | 'visual' | 'numerical' | 'realworld';

const MODES: {
  key: ExplainMode;
  label: string;
  icon: string;
  gradient: string;
  ringColor: string;
  desc: string;
}[] = [
  {
    key: 'standard',
    label: 'Step-by-Step',
    icon: 'ri-route-line',
    gradient: 'from-violet-500 to-fuchsia-500',
    ringColor: 'ring-violet-400',
    desc: 'Pure math logic',
  },
  {
    key: 'visual',
    label: 'Visual',
    icon: 'ri-eye-2-line',
    gradient: 'from-sky-500 to-cyan-500',
    ringColor: 'ring-sky-400',
    desc: 'Geometric intuition',
  },
  {
    key: 'numerical',
    label: 'Numerical',
    icon: 'ri-calculator-line',
    gradient: 'from-emerald-500 to-teal-500',
    ringColor: 'ring-emerald-400',
    desc: 'Concrete numbers',
  },
  {
    key: 'realworld',
    label: 'Real World',
    icon: 'ri-earth-line',
    gradient: 'from-orange-500 to-amber-500',
    ringColor: 'ring-orange-400',
    desc: 'Everyday analogies',
  },
];

// ─── Types ────────────────────────────────────────────────────────────────────

interface ExplainStep {
  icon: string;
  color: string;
  title: string;
  content: string;
}

interface Explanation {
  overview: string;
  steps: ExplainStep[];
  tip: string;
  difficulty: 'Basic' | 'Intermediate' | 'Advanced';
  concept: string;
}

// ─── Color Map ────────────────────────────────────────────────────────────────

const colorMap: Record<string, { bg: string; icon: string; badge: string }> = {
  violet: { bg: 'bg-violet-100', icon: 'text-violet-600', badge: 'bg-violet-600' },
  orange: { bg: 'bg-orange-100', icon: 'text-orange-600', badge: 'bg-orange-500' },
  emerald: { bg: 'bg-emerald-100', icon: 'text-emerald-600', badge: 'bg-emerald-500' },
  amber: { bg: 'bg-amber-100', icon: 'text-amber-600', badge: 'bg-amber-500' },
  teal: { bg: 'bg-teal-100', icon: 'text-teal-600', badge: 'bg-teal-500' },
  cyan: { bg: 'bg-cyan-100', icon: 'text-cyan-600', badge: 'bg-cyan-500' },
  sky: { bg: 'bg-sky-100', icon: 'text-sky-600', badge: 'bg-sky-500' },
  rose: { bg: 'bg-rose-100', icon: 'text-rose-600', badge: 'bg-rose-500' },
};

const diffBadge: Record<string, string> = {
  Basic: 'bg-emerald-100 text-emerald-700',
  Intermediate: 'bg-amber-100 text-amber-700',
  Advanced: 'bg-rose-100 text-rose-700',
};

// ─── Standard Mode Builders (original) ───────────────────────────────────────

function detectRules(query: string, content: string): string[] {
  const q = query.toLowerCase();
  const rules: string[] = [];
  if (/\*|product|\bsin\b.*\bcos\b|\bx\b.*\be\^/i.test(q)) rules.push('Product Rule');
  if (/\/|quotient|frac/i.test(q) && /(sin|cos|tan|ln)/i.test(q)) rules.push('Quotient Rule');
  if (/ln\(|e\^|sin\(x\^|cos\(x\^|\^[2-9]|sqrt\(/i.test(q)) rules.push('Chain Rule');
  if (/x\^[0-9]/i.test(q) || content.includes('power')) rules.push('Power Rule');
  if (/sin|cos|tan|sec|csc|cot/i.test(q)) rules.push('Trigonometric Differentiation');
  if (/ln|log|e\^/i.test(q)) rules.push('Logarithmic / Exponential Rule');
  return rules.length ? rules : ['Standard Differentiation'];
}

function standardDerivative(content: string, query: string): Explanation {
  const rules = detectRules(query, content);
  const hasChain = rules.includes('Chain Rule');
  const hasProduct = rules.includes('Product Rule');
  const hasPower = rules.includes('Power Rule');
  return {
    concept: 'Differentiation',
    difficulty: hasChain || hasProduct ? 'Intermediate' : 'Basic',
    overview: "The derivative measures how fast a function changes at any point — it's the instantaneous rate of change, or the slope of the tangent line at x.",
    steps: [
      { icon: 'ri-search-eye-line', color: 'violet', title: 'Identify the structure', content: `Look at the expression and identify which rules apply: ${rules.join(', ')}.` },
      hasPower ? { icon: 'ri-flashlight-line', color: 'orange', title: 'Power Rule', content: 'For xⁿ, the derivative is n·xⁿ⁻¹. Bring the exponent down and reduce it by 1.' } : null,
      hasProduct ? { icon: 'ri-links-line', color: 'emerald', title: 'Product Rule', content: "For u·v: derivative = u'·v + u·v'. Differentiate each factor, then combine." } : null,
      hasChain ? { icon: 'ri-loop-right-line', color: 'amber', title: 'Chain Rule', content: "For f(g(x)): derivative = f'(g(x)) · g'(x). Differentiate the outer, keep inner intact, multiply by inner's derivative." } : null,
      { icon: 'ri-check-double-line', color: 'teal', title: 'Simplify the result', content: 'Combine like terms and factor common expressions to write the simplest final form.' },
      { icon: 'ri-test-tube-line', color: 'cyan', title: 'Verify numerically', content: 'Compute [f(x+h) − f(x)] / h for a small h ≈ 0.0001 at any x. If it matches the derivative, you\'re correct.' },
    ].filter(Boolean) as ExplainStep[],
    tip: "The derivative of a sum is the sum of derivatives — always differentiate term-by-term and combine at the end.",
  };
}

function standardIntegral(content: string, query: string): Explanation {
  const hasByParts = /\bx\b.*e\^|e\^.*\bx\b|x.*sin|x.*cos|x.*ln/i.test(query);
  const hasDefinite = /from\s+[\d-]+\s+to|integrate.*\d.*\d/i.test(query);
  return {
    concept: 'Integration',
    difficulty: hasByParts ? 'Advanced' : hasDefinite ? 'Intermediate' : 'Basic',
    overview: "Integration is the reverse of differentiation — it finds a function F(x) such that F'(x) equals the integrand, or computes accumulated area under the curve.",
    steps: [
      { icon: 'ri-search-eye-line', color: 'violet', title: 'Choose the technique', content: hasByParts ? 'Integration by Parts: ∫u dv = uv − ∫v du. Use LIATE to choose u.' : 'Reverse Power Rule: ∫xⁿ dx = xⁿ⁺¹/(n+1) + C.' },
      hasByParts ? { icon: 'ri-links-line', color: 'orange', title: 'Set up u and dv', content: "Choose u as the LIATE-first part. Compute du and integrate dv to find v. Plug into uv − ∫v du." } : null,
      { icon: 'ri-add-circle-line', color: 'emerald', title: 'Add the constant C', content: 'Indefinite integrals always include "+ C" — the derivative of any constant is zero, so infinitely many antiderivatives exist.' },
      hasDefinite ? { icon: 'ri-ruler-2-line', color: 'amber', title: 'Apply the Fundamental Theorem', content: '∫ₐᵇ f(x) dx = F(b) − F(a). Plug in the upper limit, subtract the lower limit value.' } : null,
      { icon: 'ri-check-double-line', color: 'teal', title: 'Verify by differentiating', content: 'Take the derivative of your answer. If you recover the original integrand, the integration is correct.' },
    ].filter(Boolean) as ExplainStep[],
    tip: "LIATE order for Integration by Parts: Logarithm first, then Inverse trig, Algebraic, Trig, Exponential last.",
  };
}

function standardSolution(_content: string, query: string): Explanation {
  const isQuadratic = /x\^2|quadratic/i.test(query);
  return {
    concept: isQuadratic ? 'Quadratic Equations' : 'Equation Solving',
    difficulty: isQuadratic ? 'Intermediate' : 'Basic',
    overview: isQuadratic
      ? 'A quadratic ax² + bx + c = 0 has solutions at the x-axis crossings of the parabola. The discriminant determines whether 2, 1, or 0 real solutions exist.'
      : 'Solving an equation means isolating the variable by applying equal operations to both sides.',
    steps: [
      { icon: 'ri-equalizer-line', color: 'violet', title: 'Rearrange to standard form', content: isQuadratic ? 'Move all terms to one side: ax² + bx + c = 0. Identify a, b, c.' : 'Collect variable terms on one side, constants on the other.' },
      isQuadratic
        ? { icon: 'ri-functions-line', color: 'orange', title: 'Quadratic Formula', content: 'x = (−b ± √(b² − 4ac)) / 2a. Discriminant Δ = b² − 4ac: positive → 2 roots, zero → 1 root, negative → no real roots.' }
        : { icon: 'ri-arrow-left-right-line', color: 'orange', title: 'Isolate the variable', content: 'Add/subtract terms to both sides, then divide by the coefficient — keep the equation balanced.' },
      { icon: 'ri-test-tube-line', color: 'teal', title: 'Verify by substitution', content: 'Plug each solution back into the original equation — both sides must be equal.' },
    ].filter(Boolean) as ExplainStep[],
    tip: isQuadratic ? "Always check the discriminant first: if b² − 4ac < 0, solutions are complex numbers." : "The golden rule: whatever you do to one side, do the same to the other.",
  };
}

function standardStatistics(_content: string, _query: string): Explanation {
  return {
    concept: 'Descriptive Statistics',
    difficulty: 'Basic',
    overview: "Descriptive statistics summarise a dataset with key numbers. The mean marks the center, the standard deviation measures the spread.",
    steps: [
      { icon: 'ri-bar-chart-grouped-line', color: 'violet', title: 'Arithmetic Mean', content: 'μ = (x₁ + x₂ + … + xₙ) / n. Sum all values, divide by count. It\'s the balancing point of the data.' },
      { icon: 'ri-expand-left-right-line', color: 'orange', title: 'Standard Deviation', content: 'σ = √[Σ(xᵢ − μ)² / n]. Steps: (1) find mean, (2) subtract mean from each value, (3) square differences, (4) average them (variance), (5) take √.' },
      { icon: 'ri-sort-asc', color: 'emerald', title: 'Median vs Mean', content: "Median is the middle value after sorting — it's resistant to outliers. If mean ≫ median, data is right-skewed." },
      { icon: 'ri-information-line', color: 'amber', title: 'The 68% Rule', content: '68% of normally distributed data falls within ±1σ of the mean; 95% within ±2σ; 99.7% within ±3σ.' },
    ],
    tip: "Variance (σ²) is the standard deviation squared. Use std dev when reporting in original units — it's more interpretable.",
  };
}

function standardLimit(_content: string, query: string): Explanation {
  const isLHopital = /sin.*x|cos.*x|e\^.*x/i.test(query) && /0|infinity/i.test(query);
  return {
    concept: 'Limits',
    difficulty: isLHopital ? 'Advanced' : 'Intermediate',
    overview: "A limit describes what value a function approaches as input approaches a point — even if the function isn't defined there. Limits are the foundation of all calculus.",
    steps: [
      { icon: 'ri-arrow-right-line', color: 'violet', title: 'Try direct substitution', content: 'Plug the target x-value directly in. If the result is finite, that is the limit.' },
      { icon: 'ri-alert-line', color: 'orange', title: 'Handle indeterminate forms', content: 'If substitution gives 0/0 or ∞/∞, try factoring, rationalising, or L\'Hôpital\'s Rule.' },
      isLHopital
        ? { icon: 'ri-hospital-line', color: 'amber', title: "L'Hôpital's Rule", content: "For 0/0 or ∞/∞ forms: take the derivative of numerator and denominator separately, re-evaluate. Repeat if still indeterminate." }
        : { icon: 'ri-scissors-2-line', color: 'emerald', title: 'Factor and cancel', content: 'Factor numerator and denominator, cancel the common term causing division by zero, then substitute.' },
      { icon: 'ri-check-double-line', color: 'teal', title: 'Check both sides', content: 'lim exists only if left-hand limit (x→a⁻) equals right-hand limit (x→a⁺). If they differ, the limit does not exist.' },
    ].filter(Boolean) as ExplainStep[],
    tip: "lim_{x→0} sin(x)/x = 1 is proven via the Squeeze Theorem — NOT L'Hôpital's. One of the most important limits in calculus.",
  };
}

function standardTaylor(): Explanation {
  return {
    concept: 'Taylor / Maclaurin Series',
    difficulty: 'Advanced',
    overview: "A Taylor series expresses any smooth function as an infinite sum of polynomial terms — incredibly useful because polynomials are easy to compute, differentiate, and integrate.",
    steps: [
      { icon: 'ri-functions-line', color: 'violet', title: 'The Formula', content: 'f(x) = f(a) + f\'(a)(x−a)/1! + f\'\'(a)(x−a)²/2! + … Each term uses higher-order derivatives at x = a.' },
      { icon: 'ri-calculator-line', color: 'orange', title: 'Compute successive derivatives', content: 'Find f\'(x), f\'\'(x), f\'\'\'(x)… Evaluate each at x = a. The factorial denominator normalises the growth.' },
      { icon: 'ri-crop-line', color: 'emerald', title: 'Truncate for approximation', content: 'Use the first few terms as a polynomial approximation. More terms → better accuracy, valid near x = a.' },
      { icon: 'ri-radar-line', color: 'amber', title: 'Radius of convergence', content: 'The series equals f(x) only within radius R. For eˣ and sin(x): R = ∞. For ln(1+x): R = 1 (only |x| &lt; 1).' },
    ],
    tip: "Memorise: eˣ = 1 + x + x²/2! + x³/3! + …   sin(x) = x − x³/3! + x⁵/5! − …   cos(x) = 1 − x²/2! + x⁴/4! − …",
  };
}

function standardGeneric(podTitle: string, content: string): Explanation {
  const lines = content.split('\n').filter(l => l.trim()).slice(0, 3);
  return {
    concept: podTitle,
    difficulty: 'Intermediate',
    overview: "Here's a breakdown of what the engine did to arrive at this result.",
    steps: [
      { icon: 'ri-terminal-line', color: 'violet', title: 'Parse the input', content: 'The engine identified the mathematical structure and selected the appropriate symbolic algorithm.' },
      { icon: 'ri-cpu-line', color: 'orange', title: 'Apply the algorithm', content: 'Algebraic manipulation rules were applied symbolically, with intermediate simplifications at each step.' },
      lines.length > 0 ? { icon: 'ri-check-double-line', color: 'emerald', title: 'Result obtained', content: `Key output: ${lines[0]}${lines[1] ? ' → ' + lines[1] : ''}` } : null,
      { icon: 'ri-lightbulb-line', color: 'amber', title: 'Interpretation', content: 'Review each row and compare against known formulas or theorems to deepen understanding.' },
    ].filter(Boolean) as ExplainStep[],
    tip: "Substitute simple numbers (x=0 or x=1) to quickly verify any symbolic result.",
  };
}

// ─── Visual Mode Builders ─────────────────────────────────────────────────────

function visualDerivative(): Explanation {
  return {
    concept: 'Differentiation — Visual',
    difficulty: 'Basic',
    overview: "Picture zooming into the graph of f(x) at any point. As you zoom further, the curve looks like a perfectly straight line. The slope of that line — how steep it is — is exactly the derivative at that point.",
    steps: [
      { icon: 'ri-line-chart-line', color: 'sky', title: 'Draw the curve', content: 'Sketch y = f(x) on a coordinate plane. Pick any point P = (x, f(x)) on the curve. This is where we want the derivative.' },
      { icon: 'ri-slash-commands', color: 'cyan', title: 'Draw a secant line', content: 'Pick another point Q = (x+h, f(x+h)) nearby. The line through P and Q has slope [f(x+h) − f(x)]/h. This approximates the derivative.' },
      { icon: 'ri-zoom-in-line', color: 'violet', title: 'Slide Q toward P', content: 'Move Q closer and closer to P (h → 0). Watch the secant line rotate. It converges to a single line that just touches the curve at P — the tangent line.' },
      { icon: 'ri-focus-2-line', color: 'teal', title: 'Tangent slope = derivative', content: 'The slope of that tangent line is f\'(x). It tells you: if x nudges forward by ε, then f(x) changes by approximately f\'(x)·ε.' },
      { icon: 'ri-arrow-up-down-line', color: 'amber', title: 'Positive vs negative derivative', content: 'f\'(x) > 0 → curve going uphill. f\'(x) < 0 → downhill. f\'(x) = 0 → flat top or bottom (local max/min).' },
    ],
    tip: "Try this: graph y = x² and draw tangent lines at x = −2, 0, 2. The slopes are −4, 0, and 4. You're literally reading the derivative from the picture.",
  };
}

function visualIntegral(): Explanation {
  return {
    concept: 'Integration — Visual',
    difficulty: 'Basic',
    overview: "Picture the area under the curve y = f(x) between two x-values. Fill that region with vertical rectangles. The thinner the rectangles, the more perfectly they fill the space. Integration is making those rectangles infinitely thin.",
    steps: [
      { icon: 'ri-paint-fill', color: 'sky', title: 'Shade the region', content: 'Sketch y = f(x). Shade the area between the curve and the x-axis from x = a to x = b. This coloured region is what you\'re computing.' },
      { icon: 'ri-bar-chart-box-line', color: 'cyan', title: 'Fill with n rectangles', content: 'Divide [a, b] into n equal strips of width Δx = (b−a)/n. On each strip, draw a rectangle with height f(xᵢ). Approximate area ≈ Σ f(xᵢ)·Δx.' },
      { icon: 'ri-zoom-in-line', color: 'violet', title: 'Double n, watch it improve', content: 'Use 10, then 100, then 1000 rectangles. The gaps disappear. The approximation converges to the true area. This sum is a Riemann sum.' },
      { icon: 'ri-infinity', color: 'teal', title: 'Take n → ∞', content: 'As n → ∞ and Δx → 0, the sum becomes exact. That is the definite integral — the exact signed area under the curve.' },
      { icon: 'ri-minus-circle-line', color: 'amber', title: 'Signed area matters', content: 'Area below the x-axis counts as negative. The integral gives net area. To find total area, integrate |f(x)| or split at zeros.' },
    ],
    tip: "You can literally draw this on graph paper for simple functions. Count the grid squares under the curve — that's a Riemann sum approximation.",
  };
}

function visualSolution(): Explanation {
  return {
    concept: 'Equation Solving — Visual',
    difficulty: 'Basic',
    overview: "Solving f(x) = 0 visually means asking: where does the graph of y = f(x) cross the x-axis? Each crossing point is a solution. For a quadratic, you're looking for where a parabola lands on the ground.",
    steps: [
      { icon: 'ri-line-chart-line', color: 'sky', title: 'Plot the function', content: 'Sketch y = f(x). Every point on the curve is a (x, f(x)) pair. Solutions are where y = 0 — the x-axis.' },
      { icon: 'ri-focus-2-line', color: 'cyan', title: 'Find x-axis crossings', content: 'Where does the curve cross the x-axis? Each crossing is a root: f(root) = 0 by definition. A parabola can cross 0, 1, or 2 times.' },
      { icon: 'ri-node-tree', color: 'violet', title: 'Discriminant as shape info', content: 'Discriminant Δ = b² − 4ac. Δ > 0: parabola crosses x-axis twice (two real roots). Δ = 0: just touches it (one repeated root). Δ < 0: floats above or below (no real roots).' },
      { icon: 'ri-check-double-line', color: 'teal', title: 'Read the solutions', content: 'The x-coordinates of the crossing points are your solutions. You can estimate them from the graph and verify algebraically.' },
    ],
    tip: "A great visual check: graph y = x² − 5x + 6. You should see two x-intercepts at x = 2 and x = 3. The vertex (minimum) is at x = 2.5.",
  };
}

function visualStatistics(): Explanation {
  return {
    concept: 'Statistics — Visual',
    difficulty: 'Basic',
    overview: "Imagine all your data points plotted on a number line as dots. The mean is the balancing point — if the line were a seesaw, the mean is where you'd put the fulcrum. Standard deviation is the typical distance each dot is from that balance point.",
    steps: [
      { icon: 'ri-layout-left-2-line', color: 'sky', title: 'Plot the data', content: 'Draw a number line. Place each data value as a dot. You can immediately see where the values cluster and how spread out they are.' },
      { icon: 'ri-scales-3-line', color: 'cyan', title: 'Find the balancing point (Mean)', content: "The mean is the center of gravity. If each dot had equal weight, the number line would balance at x = mean. Points on both sides exactly cancel out." },
      { icon: 'ri-expand-left-right-line', color: 'violet', title: 'Measure the spread (Std Dev)', content: 'Draw a "typical distance" arrow from the mean to a representative dot. That distance is approximately σ. Most dots (68%) lie within one arrow-length of center.' },
      { icon: 'ri-bell-line', color: 'teal', title: 'The bell curve emerges', content: 'With enough data, the dot plot turns into a bell curve — tall and narrow for small σ (tightly packed), short and wide for large σ (spread out).' },
    ],
    tip: "For a quick visual: a histogram with a tall, narrow peak = small std dev. A flat, wide histogram = large std dev. Shape tells the story.",
  };
}

function visualLimit(): Explanation {
  return {
    concept: 'Limits — Visual',
    difficulty: 'Intermediate',
    overview: "Imagine walking along the curve y = f(x) toward the point x = a from both the left and the right. If your height (y value) converges to the same value from both directions, that value is the limit — even if there's a hole in the curve at that exact spot.",
    steps: [
      { icon: 'ri-walk-line', color: 'sky', title: 'Approach from the left', content: 'Start at x = a − 1 and slide right toward x = a. Watch your y-value. Where does it seem to be heading? That\'s the left-hand limit.' },
      { icon: 'ri-arrow-left-right-line', color: 'cyan', title: 'Approach from the right', content: 'Now start at x = a + 1 and slide left. Again watch your y-value. Where does it head? That\'s the right-hand limit.' },
      { icon: 'ri-merge-cells-horizontal', color: 'violet', title: 'Do they meet?', content: 'If both paths converge to the same height, the limit exists and equals that height — even if there\'s a hole or undefined point at x = a exactly.' },
      { icon: 'ri-scissors-cut-line', color: 'amber', title: 'When they don\'t meet', content: 'If left and right approaches converge to different heights, the limit does not exist (DNE). Classic example: f(x) = 1/x at x = 0 — left goes to −∞, right goes to +∞.' },
    ],
    tip: "Use Desmos or any graphing tool: zoom into the target x-value and watch the curve. This visual is worth a hundred algebraic manipulations.",
  };
}

function visualTaylor(): Explanation {
  return {
    concept: 'Taylor Series — Visual',
    difficulty: 'Advanced',
    overview: "A Taylor series is a polynomial that dresses itself up as another function. Picture overlaying increasingly curvy polynomial approximations onto a smooth function. Each new term adds another 'bend', making the fit tighter and tighter near x = a.",
    steps: [
      { icon: 'ri-drag-drop-line', color: 'sky', title: 'Start with a tangent line (1 term)', content: 'The first Taylor approximation is just the tangent line at x = a: f(a) + f\'(a)(x−a). It matches value and slope at x = a, but diverges quickly.' },
      { icon: 'ri-curves-line', color: 'cyan', title: 'Add a quadratic bend (2 terms)', content: 'Adding the f\'\'(a)(x−a)²/2! term makes the approximation curve. Now it matches value, slope, and curvature at x = a. The fit improves.' },
      { icon: 'ri-pulse-line', color: 'violet', title: 'Each new term adds a wiggle', content: 'Every additional term matches one more derivative at x = a. Plot sinusoidal approximations with 3, 5, 7 terms and watch them converge to the true curve over a wider range.' },
      { icon: 'ri-radar-line', color: 'amber', title: 'The convergence radius', content: 'Beyond the convergence radius R, the polynomial breaks away from the function. Visually: the approximation is great near x = a but explodes as you move far away.' },
    ],
    tip: "In Desmos, type y = sin(x). Then overlay y = x, y = x − x³/6, y = x − x³/6 + x⁵/120. Watch each polynomial hug the sine curve over a wider and wider range.",
  };
}

// ─── Numerical Mode Builders ──────────────────────────────────────────────────

function numericalDerivative(_content: string, query: string): Explanation {
  const isSquare = /x\^2|x\*x/i.test(query);
  const isCubic = /x\^3/i.test(query);
  const fn = isSquare ? 'x²' : isCubic ? 'x³' : 'f(x)';
  const atX = 3;
  const numResult = isSquare ? '6' : isCubic ? '27' : 'f\'(3)';
  return {
    concept: 'Differentiation — Numerical',
    difficulty: 'Basic',
    overview: `Let's ground the derivative in real numbers. We'll verify it using the finite-difference approximation, which is how computers often compute derivatives.`,
    steps: [
      { icon: 'ri-number-1', color: 'emerald', title: `Choose a test point: x = ${atX}`, content: `We'll verify the derivative of ${fn} at x = ${atX}. A specific number makes the concept concrete.` },
      { icon: 'ri-calculator-line', color: 'teal', title: 'Compute the finite difference', content: `Use h = 0.001: slope ≈ [f(${atX}+h) − f(${atX})] / h. With h = 0.001: numerator ≈ ${isSquare ? '6.001' : isCubic ? '27.027' : 'a small number'}. Divide by 0.001 → ≈ ${isSquare ? '6.001' : isCubic ? '27.027' : 'the slope'}.` },
      { icon: 'ri-check-line', color: 'cyan', title: `Analytical answer: ${numResult}`, content: `The exact derivative by the Power Rule gives ${numResult} at x = ${atX}. Our numerical estimate matches to 3 decimal places — the remaining error is just because h wasn't truly zero.` },
      { icon: 'ri-zoom-in-line', color: 'violet', title: 'Make h smaller, get more accurate', content: `h = 0.0001 → error ≈ 0.0001. h = 0.00001 → error ≈ 0.00001. The derivative is the limit as h → 0 of this approximation.` },
    ],
    tip: `In Python: import numpy as np; h=1e-7; x=3; print((f(x+h)-f(x))/h). This gives the numerical derivative to ~7 significant figures.`,
  };
}

function numericalIntegral(_content: string, query: string): Explanation {
  const hasDefinite = /from\s+[\d-]+\s+to|integrate.*\d/i.test(query);
  return {
    concept: 'Integration — Numerical',
    difficulty: 'Intermediate',
    overview: `Let's compute ∫₀¹ x² dx numerically using Riemann sums, then compare to the exact analytical answer.`,
    steps: [
      { icon: 'ri-number-1', color: 'emerald', title: 'Set up 3 rectangles (n = 3)', content: 'Δx = 1/3. Midpoints: x = 1/6, 1/2, 5/6. Heights: f(1/6) = 0.028, f(1/2) = 0.25, f(5/6) = 0.694. Sum = (0.028 + 0.25 + 0.694) × 1/3 ≈ 0.324.' },
      { icon: 'ri-add-line', color: 'teal', title: 'Use 10 rectangles (n = 10)', content: 'Δx = 0.1. Compute f at midpoints 0.05, 0.15, …, 0.95. Sum × 0.1 ≈ 0.3325. Closer! Each doubling of n cuts the error roughly in half.' },
      { icon: 'ri-infinity', color: 'cyan', title: 'Exact answer: 1/3 = 0.3333…', content: hasDefinite ? 'By the Reverse Power Rule: ∫x² dx = x³/3. Evaluate: [1³/3 − 0³/3] = 1/3 ≈ 0.3333. Our n=10 estimate was off by only 0.001.' : '∫₀¹ x² dx = [x³/3]₀¹ = 1/3 − 0 = 1/3 ≈ 0.3333.' },
      { icon: 'ri-calculator-line', color: 'violet', title: 'Why numerical integration matters', content: "Functions like e^(−x²) have NO closed-form antiderivative. We MUST use numerical methods (Gaussian quadrature, Simpson's rule) to compute their integrals." },
    ],
    tip: "Simpson's Rule is much more accurate than rectangles for smooth functions: ∫f dx ≈ (Δx/3)[f(x₀) + 4f(x₁) + 2f(x₂) + 4f(x₃) + … + f(xₙ)]. Error drops as h⁴, not h².",
  };
}

function numericalSolution(_content: string, query: string): Explanation {
  const isQuadratic = /x\^2/i.test(query);
  return {
    concept: 'Equation Solving — Numerical',
    difficulty: 'Intermediate',
    overview: `Let's verify the solution numerically. We'll use substitution to confirm the answer is correct, then show how Newton's Method would find it if we didn't have an algebraic formula.`,
    steps: [
      { icon: 'ri-number-1', color: 'emerald', title: 'Verify by substitution', content: isQuadratic ? 'For x² − 5x + 6 = 0, try x = 2: 4 − 10 + 6 = 0 ✓. Try x = 3: 9 − 15 + 6 = 0 ✓. Both check out.' : 'Substitute the solution back: plug it in and confirm both sides are equal.' },
      { icon: 'ri-loop-right-line', color: 'teal', title: "Newton's Method (numerical solver)", content: "For any f(x) = 0: start with a guess x₀, then iterate: x_{n+1} = xₙ − f(xₙ)/f'(xₙ). Each iteration roughly doubles the significant digits." },
      { icon: 'ri-calculator-line', color: 'cyan', title: 'Example iteration', content: isQuadratic ? "Guess x₀ = 1. f(1) = −2, f'(1) = −3. x₁ = 1 − (−2)/(−3) = 0.33 (heading toward x = 2 but from the wrong direction). Try x₀ = 2.5 → converges to 2 or 3 depending on starting side." : "One Newton step: new_guess = old_guess − f(old_guess)/f'(old_guess). Repeat until f(guess) ≈ 0." },
      { icon: 'ri-check-double-line', color: 'violet', title: 'When exact formulas fail', content: "For degree ≥ 5, there's NO general algebraic formula (Abel's theorem). Newton's Method is how we solve x⁵ − x + 0.1 = 0 numerically." },
    ],
    tip: "Excel/Python can find roots: scipy.optimize.fsolve(f, x0=2) gives x = 2.0 for our example. The computer is running Newton's Method (or a variant) under the hood.",
  };
}

function numericalStatistics(_content: string, _query: string): Explanation {
  return {
    concept: 'Statistics — Numerical',
    difficulty: 'Basic',
    overview: "Let's walk through descriptive statistics with a concrete dataset: {2, 4, 4, 4, 5, 5, 7, 9}. These 8 values give an especially clean example.",
    steps: [
      { icon: 'ri-number-1', color: 'emerald', title: 'Mean: μ = 5.0', content: 'Sum = 2+4+4+4+5+5+7+9 = 40. Count = 8. Mean = 40/8 = 5.0. This is the balancing point.' },
      { icon: 'ri-calculator-line', color: 'teal', title: 'Deviations: xᵢ − μ', content: 'Differences from mean: −3, −1, −1, −1, 0, 0, +2, +4. Notice they sum to zero — they must, by definition of the mean.' },
      { icon: 'ri-expand-left-right-line', color: 'cyan', title: 'Standard Deviation: σ = 2.0', content: 'Square deviations: 9,1,1,1,0,0,4,16. Sum = 32. Variance = 32/8 = 4. σ = √4 = 2.0 exactly.' },
      { icon: 'ri-sort-asc', color: 'violet', title: 'Median: 4.5', content: 'Sorted: 2,4,4,4,5,5,7,9. Even count, so median = average of 4th and 5th values = (4+5)/2 = 4.5. Mean (5.0) > Median (4.5) → slight right skew.' },
    ],
    tip: "This dataset (Karl Pearson's famous example) has σ = 2.0 exactly. 6 of 8 values (75%) lie within ±σ of the mean — close to the 68% rule for normal distributions.",
  };
}

function numericalLimit(_content: string, query: string): Explanation {
  const isSinX = /sin.*\/.*x|sin\(x\).*x/i.test(query);
  return {
    concept: 'Limits — Numerical',
    difficulty: 'Intermediate',
    overview: `Let's evaluate lim_{x→0} sin(x)/x by computing it at values closer and closer to 0. This famous limit = 1 and is the foundation of all sine-related calculus.`,
    steps: [
      { icon: 'ri-number-1', color: 'emerald', title: 'x = 0.1: sin(0.1)/0.1', content: 'sin(0.1) ≈ 0.09983. Ratio = 0.09983/0.1 = 0.9983. Already very close to 1.' },
      { icon: 'ri-zoom-in-line', color: 'teal', title: 'x = 0.01: sin(0.01)/0.01', content: 'sin(0.01) ≈ 0.009999833. Ratio = 0.999983. Even closer to 1.' },
      { icon: 'ri-zoom-in-line', color: 'cyan', title: 'x = 0.001: ratio = 0.9999998', content: 'The pattern is clear — as x → 0, sin(x)/x → 1. Direct substitution fails (0/0), but the table shows the limit is exactly 1.' },
      { icon: 'ri-award-line', color: 'violet', title: isSinX ? 'Confirmed: limit = 1' : 'General numerical approach', content: isSinX ? 'The Squeeze Theorem proves this rigorously: x·cos(x) ≤ sin(x) ≤ x for x > 0. Divide by x and squeeze.' : 'Build a table of values approaching the target x. If values converge, that\'s your limit. This works even when algebra fails.' },
    ],
    tip: "This limit explains why d/dx[sin(x)] = cos(x). The derivative of sin is computed using lim sin(x)/x = 1 as a key step in the proof.",
  };
}

function numericalTaylor(): Explanation {
  return {
    concept: 'Taylor Series — Numerical',
    difficulty: 'Advanced',
    overview: "Let's evaluate e¹ = e ≈ 2.71828 by computing the Maclaurin series term by term. We'll watch how the partial sums converge to the exact value.",
    steps: [
      { icon: 'ri-number-1', color: 'emerald', title: '1 term: 1.0 (error = 1.718)', content: 'eˣ ≈ 1 (just the constant). At x=1: sum = 1.000. Error = 1.718.' },
      { icon: 'ri-add-line', color: 'teal', title: '3 terms: 2.5 (error = 0.218)', content: '1 + 1 + 1/2 = 2.5. Getting closer. Each new term adds one more bend to the polynomial.' },
      { icon: 'ri-add-line', color: 'cyan', title: '5 terms: 2.7083 (error = 0.010)', content: '1 + 1 + 0.5 + 0.1667 + 0.0417 = 2.7083. Nearly there.' },
      { icon: 'ri-check-double-line', color: 'violet', title: '10 terms: 2.71828 (error < 0.000003)', content: 'With 10 terms we have 6 correct decimal places. Adding more terms gives more digits. This IS how computers calculate eˣ — as a truncated series.' },
    ],
    tip: "In Python: sum(1/math.factorial(k) for k in range(10)) = 2.7182815255... vs math.e = 2.7182818284... Just 10 terms gives 6 correct digits!",
  };
}

// ─── Real-World Mode Builders ─────────────────────────────────────────────────

function realworldDerivative(): Explanation {
  return {
    concept: 'Differentiation — Real World',
    difficulty: 'Basic',
    overview: "Your car's speedometer shows the derivative of your position. Every moment, it measures how fast your distance from home is changing — that's instantaneous velocity, the derivative of d(t).",
    steps: [
      { icon: 'ri-car-line', color: 'orange', title: 'Speed = derivative of position', content: "If d(t) is your distance from home at time t, then d'(t) is your speed. At 60 mph you're moving 60 miles/hour — a derivative value." },
      { icon: 'ri-dashboard-line', color: 'amber', title: 'Acceleration = second derivative', content: "Press the accelerator pedal and your speed changes — that change is acceleration = d''(t). The jerk you feel is d'''(t), the third derivative." },
      { icon: 'ri-money-dollar-circle-line', color: 'violet', title: 'Marginal profit in economics', content: "A company models profit P(x) for x units sold. P'(x) is the marginal profit — how much extra profit from selling one more unit. Set P'(x) = 0 to find the production optimum." },
      { icon: 'ri-heart-pulse-line', color: 'rose', title: 'Drug clearance in medicine', content: "The rate at which a drug leaves your bloodstream is the derivative of concentration C(t): C'(t) is negative (decreasing). Pharmacists use this to time doses." },
    ],
    tip: "NASA engineers compute rocket trajectories using thousands of derivatives per second. Every control system — from airplane autopilots to phone accelerometers — is doing derivative math.",
  };
}

function realworldIntegral(): Explanation {
  return {
    concept: 'Integration — Real World',
    difficulty: 'Basic',
    overview: "Every time you fill a bathtub, you're computing an integral. The water flow rate changes over time (you adjust the tap), and the total water in the tub is the accumulated sum of all that flow — an integral.",
    steps: [
      { icon: 'ri-water-flash-line', color: 'orange', title: 'Water tank filling', content: "Flow rate r(t) in litres/minute. Total water after T minutes = ∫₀ᵀ r(t) dt. Even if you keep adjusting the tap, integration tracks the total volume." },
      { icon: 'ri-car-line', color: 'amber', title: 'Distance from speed', content: "Know your speed v(t) at every moment? Distance traveled = ∫v(t) dt. Your GPS does this continuously — integrating your speed to track position." },
      { icon: 'ri-flashlight-line', color: 'violet', title: 'Electricity bill', content: "Your power consumption P(t) varies every hour. Total energy used = ∫P(t) dt (measured in kilowatt-hours). Your electricity meter is literally computing an integral 24/7." },
      { icon: 'ri-heart-pulse-line', color: 'rose', title: 'Medical IV drip', content: "Medication drips at a variable rate into the bloodstream. Total dose received = ∫rate(t) dt. Nurses monitor this integral to ensure correct dosing." },
    ],
    tip: "Engineers use numerical integration constantly because real-world data (sensor readings) doesn't have a clean formula — you just integrate the measurements.",
  };
}

function realworldSolution(): Explanation {
  return {
    concept: 'Equation Solving — Real World',
    difficulty: 'Basic',
    overview: "Solving equations is how engineers, scientists, and business analysts find the exact values that make a system work. The break-even point, the optimal bridge arch, the drug dose — they're all equation solutions.",
    steps: [
      { icon: 'ri-store-line', color: 'orange', title: 'Business break-even', content: "Revenue R(x) = 15x. Cost C(x) = 10x + 2000. Break-even: R = C → 15x = 10x + 2000 → x = 400 units. Solving this equation tells the business exactly how many units to sell before making profit." },
      { icon: 'ri-ball-pen-line', color: 'amber', title: 'Projectile motion', content: "A ball thrown at 20 m/s from a 45m cliff: height h(t) = 45 + 20t − 4.9t². When does it land? Solve h(t) = 0. The quadratic formula gives the landing time." },
      { icon: 'ri-anchor-line', color: 'violet', title: 'Structural engineering', content: "A beam bends under load. The deflection at position x satisfies an equation. Solving it tells engineers exactly where maximum stress occurs — critical for safety." },
      { icon: 'ri-flask-line', color: 'rose', title: 'Chemical equilibrium', content: "In chemistry, equilibrium concentrations satisfy Kc = [products]/[reactants]. Solving this equation tells you exactly how much product forms when the reaction stabilises." },
    ],
    tip: "Every time a GPS calculates your route, it solves thousands of equations simultaneously (shortest path algorithms). Equation solving is the computational backbone of the modern world.",
  };
}

function realworldStatistics(): Explanation {
  return {
    concept: 'Statistics — Real World',
    difficulty: 'Basic',
    overview: "Statistics is the language of decisions under uncertainty. Every quality control system, every medical trial, every Netflix recommendation, every financial model is built on the same formulas you just computed.",
    steps: [
      { icon: 'ri-school-line', color: 'orange', title: 'Test scores', content: "Class of 30 students scores exam. Mean = 72 tells the class average. Std dev = 8 tells you that most students (68%) scored between 64 and 80. A student at 90 is 2.25 standard deviations above average." },
      { icon: 'ri-medicine-bottle-line', color: 'amber', title: 'Drug trials', content: "A new drug is tested on 1000 patients. Mean blood pressure reduction = 12 mmHg, std dev = 4. The overlapping distributions of treated vs untreated groups determine statistical significance (p-value)." },
      { icon: 'ri-stock-line', color: 'violet', title: 'Finance: Sharpe ratio', content: "In investing, std dev of returns = volatility = risk. Expected return / std dev = Sharpe Ratio. A portfolio with mean return 10% and σ = 5% has ratio 2 — good. Lower std dev for same return = better risk-adjusted performance." },
      { icon: 'ri-cpu-line', color: 'rose', title: 'Quality control', content: "A factory producing bolts targets diameter 10mm with σ = 0.1mm. Any bolt outside ±3σ (9.7–10.3mm) is rejected. The '3-sigma rule' (Six Sigma) is used by Toyota, Boeing, Apple." },
    ],
    tip: "Normal distribution is everywhere: heights, IQ scores, measurement errors, stock returns (approximately). If a dataset follows a bell curve, mean and std dev tell you almost everything.",
  };
}

function realworldLimit(): Explanation {
  return {
    concept: 'Limits — Real World',
    difficulty: 'Intermediate',
    overview: "Limits describe what happens 'in the long run' or 'at the edge of the possible.' They appear whenever a system approaches but never quite reaches some threshold — terminal velocity, market saturation, battery charge.",
    steps: [
      { icon: 'ri-parachute-line', color: 'orange', title: 'Terminal velocity', content: "A falling object accelerates until air resistance equals gravity. Speed v(t) has a limit: lim_{t→∞} v(t) = terminal velocity. The object never quite reaches it, but gets arbitrarily close." },
      { icon: 'ri-bar-chart-box-line', color: 'amber', title: 'Market saturation', content: "A product's market share M(t) = 100 × (1 − e^(−kt)). As t → ∞, M → 100% — but it never quite gets there. The limit is the theoretical maximum market share." },
      { icon: 'ri-battery-charge-line', color: 'violet', title: 'Battery charging', content: "Charge percentage C(t) = 100(1 − e^(−t/τ)). It approaches 100% asymptotically — that's why a 'full charge' is defined at 99% not 100%. Limit = 100%, never actually reached." },
      { icon: 'ri-git-branch-line', color: 'rose', title: 'Compound interest: e', content: "Compound interest: $1 at 100% interest. Monthly: (1+1/12)^12 = 2.613. Daily: (1+1/365)^365 = 2.714. Continuous: lim_{n→∞} (1+1/n)^n = e = 2.71828… The limit IS Euler's number." },
    ],
    tip: "The concept of a limit was invented to solve the paradox Zeno posed 2,500 years ago: how can Achilles ever catch the tortoise if he must first cross infinitely many halfway points? The limit says: those infinitely many steps sum to a finite time.",
  };
}

function realworldTaylor(): Explanation {
  return {
    concept: 'Taylor Series — Real World',
    difficulty: 'Advanced',
    overview: "Your phone's calculator, GPS, and graphics processor all use Taylor series internally. Computing sin(1.23) or ln(0.97) with a sequence of multiplications is much faster than any other method — that's why Taylor series are fundamental to computer science.",
    steps: [
      { icon: 'ri-smartphone-line', color: 'orange', title: 'Your calculator uses this', content: "When you type sin(1.23) on a calculator, it evaluates the series x − x³/6 + x⁵/120 − … internally. The CPU only knows multiplication and addition — the series converts trig functions into those primitive operations." },
      { icon: 'ri-gps-line', color: 'amber', title: 'GPS coordinate math', content: "GPS must convert between coordinate systems thousands of times per second. It uses polynomial (Taylor) approximations of trigonometric functions to keep the math fast enough for real-time navigation." },
      { icon: 'ri-robot-line', color: 'violet', title: 'Neural network activations', content: "The sigmoid function σ(x) = 1/(1+e^{−x}) in deep learning is often approximated by its Taylor series near x=0 for faster computation on specialized chips." },
      { icon: 'ri-global-line', color: 'rose', title: 'Einstein\'s correction to Newton', content: "Relativistic kinetic energy = mc²(1/√(1−v²/c²) − 1). Taylor expand for v ≪ c: first term is ½mv² (Newton's formula!). Einstein's equation IS Newton's equation plus small correction terms." },
    ],
    tip: "The CORDIC algorithm (used in virtually every calculator since the 1970s) computes sin, cos, and tan using only shifts and additions — essentially a clever Taylor series implementation in hardware.",
  };
}

// ─── Dispatcher ───────────────────────────────────────────────────────────────

function generateExplanation(pod: ResultPod, originalQuery: string, mode: ExplainMode): Explanation {
  const t = pod.title.toLowerCase();
  const content = pod.subpods.map(s => s.plaintext ?? '').join('\n');

  if (mode === 'standard') {
    if (t.includes('derivative')) return standardDerivative(content, originalQuery);
    if (t.includes('integral') || t.includes('antiderivat')) return standardIntegral(content, originalQuery);
    if (t === 'solutions' || t === 'solution' || t.includes('roots')) return standardSolution(content, originalQuery);
    if (t.includes('statistic') || t.includes('mean') || t.includes('deviation')) return standardStatistics(content, originalQuery);
    if (t.includes('limit')) return standardLimit(content, originalQuery);
    if (t.includes('taylor') || t.includes('series')) return standardTaylor();
    return standardGeneric(pod.title, content);
  }

  if (mode === 'visual') {
    if (t.includes('derivative')) return visualDerivative();
    if (t.includes('integral') || t.includes('antiderivat')) return visualIntegral();
    if (t === 'solutions' || t === 'solution' || t.includes('roots')) return visualSolution();
    if (t.includes('statistic') || t.includes('mean') || t.includes('deviation')) return visualStatistics();
    if (t.includes('limit')) return visualLimit();
    if (t.includes('taylor') || t.includes('series')) return visualTaylor();
    return visualDerivative(); // fallback
  }

  if (mode === 'numerical') {
    if (t.includes('derivative')) return numericalDerivative(content, originalQuery);
    if (t.includes('integral') || t.includes('antiderivat')) return numericalIntegral(content, originalQuery);
    if (t === 'solutions' || t === 'solution' || t.includes('roots')) return numericalSolution(content, originalQuery);
    if (t.includes('statistic') || t.includes('mean') || t.includes('deviation')) return numericalStatistics(content, originalQuery);
    if (t.includes('limit')) return numericalLimit(content, originalQuery);
    if (t.includes('taylor') || t.includes('series')) return numericalTaylor();
    return numericalDerivative(content, originalQuery);
  }

  // realworld
  if (t.includes('derivative')) return realworldDerivative();
  if (t.includes('integral') || t.includes('antiderivat')) return realworldIntegral();
  if (t === 'solutions' || t === 'solution' || t.includes('roots')) return realworldSolution();
  if (t.includes('statistic') || t.includes('mean') || t.includes('deviation')) return realworldStatistics();
  if (t.includes('limit')) return realworldLimit();
  if (t.includes('taylor') || t.includes('series')) return realworldTaylor();
  return realworldDerivative();
}

// ─── Component ────────────────────────────────────────────────────────────────

interface StepExplainerProps {
  pod: ResultPod;
  originalQuery: string;
  onClose: () => void;
}

export default function StepExplainer({ pod, originalQuery, onClose }: StepExplainerProps) {
  const [mode, setMode] = useState<ExplainMode>('standard');
  const [visibleSteps, setVisibleSteps] = useState(0);
  const [animKey, setAnimKey] = useState(0);

  const explanation = generateExplanation(pod, originalQuery, mode);
  const activeModeMeta = MODES.find(m => m.key === mode)!;

  // Re-animate steps whenever mode changes
  useEffect(() => {
    setVisibleSteps(0);
    const id = setInterval(() => {
      setVisibleSteps(v => {
        if (v >= explanation.steps.length) { clearInterval(id); return v; }
        return v + 1;
      });
    }, 200);
    return () => clearInterval(id);
  }, [animKey, explanation.steps.length]);

  const cycleMode = useCallback(() => {
    const idx = MODES.findIndex(m => m.key === mode);
    const next = MODES[(idx + 1) % MODES.length].key;
    setMode(next);
    setAnimKey(k => k + 1);
  }, [mode]);

  const switchMode = useCallback((m: ExplainMode) => {
    if (m === mode) return;
    setMode(m);
    setAnimKey(k => k + 1);
  }, [mode]);

  const primaryContent = pod.subpods.find(s => s.plaintext)?.plaintext ?? '';

  const modeHeaderGradient =
    mode === 'visual' ? 'from-sky-600 to-cyan-600'
    : mode === 'numerical' ? 'from-emerald-600 to-teal-600'
    : mode === 'realworld' ? 'from-orange-500 to-amber-500'
    : 'from-violet-600 to-fuchsia-600';

  const modeOverviewBg =
    mode === 'visual' ? 'from-sky-50 to-cyan-50 border-sky-200'
    : mode === 'numerical' ? 'from-emerald-50 to-teal-50 border-emerald-200'
    : mode === 'realworld' ? 'from-orange-50 to-amber-50 border-orange-200'
    : 'from-violet-50 to-fuchsia-50 border-violet-200';

  const modeOverviewText =
    mode === 'visual' ? 'text-sky-700'
    : mode === 'numerical' ? 'text-emerald-700'
    : mode === 'realworld' ? 'text-orange-700'
    : 'text-violet-700';

  const modeOverviewIcon =
    mode === 'visual' ? 'ri-eye-2-line text-sky-500'
    : mode === 'numerical' ? 'ri-calculator-line text-emerald-500'
    : mode === 'realworld' ? 'ri-earth-line text-orange-500'
    : 'ri-book-open-line text-violet-500';

  const modeTipBg =
    mode === 'visual' ? 'bg-sky-50 border-sky-200'
    : mode === 'numerical' ? 'bg-emerald-50 border-emerald-200'
    : mode === 'realworld' ? 'bg-orange-50 border-orange-200'
    : 'bg-amber-50 border-amber-200';

  const modeTipIcon =
    mode === 'visual' ? 'bg-sky-100 ri-telescope-line text-sky-500'
    : mode === 'numerical' ? 'bg-emerald-100 ri-function-line text-emerald-500'
    : mode === 'realworld' ? 'bg-orange-100 ri-rocket-line text-orange-500'
    : 'bg-amber-100 ri-lightbulb-flash-line text-amber-500';

  const modeTipLabel =
    mode === 'visual' ? 'text-sky-700'
    : mode === 'numerical' ? 'text-emerald-700'
    : mode === 'realworld' ? 'text-orange-700'
    : 'text-amber-700';

  const modeTipText =
    mode === 'visual' ? 'text-sky-800'
    : mode === 'numerical' ? 'text-emerald-800'
    : mode === 'realworld' ? 'text-orange-800'
    : 'text-amber-800';

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-6">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">

        {/* ── Header ── */}
        <div className={`bg-gradient-to-r ${modeHeaderGradient} px-6 py-5 flex items-center justify-between transition-all duration-300`}>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-white/20 rounded-xl flex items-center justify-center">
              <i className={`${activeModeMeta.icon} text-white text-lg`}></i>
            </div>
            <div>
              <div className="font-bold text-white text-base">Step Explainer</div>
              <div className="text-white/70 text-xs">{pod.title} — {activeModeMeta.label}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${diffBadge[explanation.difficulty]}`}>
              {explanation.difficulty}
            </span>
            <button
              onClick={onClose}
              className="w-9 h-9 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 transition-colors text-white cursor-pointer ml-1"
            >
              <i className="ri-close-line text-lg"></i>
            </button>
          </div>
        </div>

        {/* ── Mode Selector ── */}
        <div className="flex items-center gap-1.5 px-5 py-3 border-b border-slate-100 bg-slate-50/80 flex-shrink-0 flex-wrap">
          {MODES.map(m => (
            <button
              key={m.key}
              onClick={() => switchMode(m.key)}
              title={m.desc}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
                mode === m.key
                  ? `bg-gradient-to-r ${m.gradient} text-white shadow-sm`
                  : 'bg-white border border-slate-200 text-slate-500 hover:text-slate-700 hover:border-slate-300'
              }`}
            >
              <i className={`${m.icon} text-[11px]`}></i>
              {m.label}
            </button>
          ))}
          <div className="flex-1 text-right">
            <span className="text-[10px] text-slate-400 italic">{activeModeMeta.desc}</span>
          </div>
        </div>

        {/* ── Scrollable Body ── */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">

          {/* Result preview */}
          <div className="bg-slate-50 border border-slate-200 rounded-xl px-5 py-3 flex items-start gap-3">
            <div className="w-7 h-7 bg-gradient-to-br from-orange-500 to-red-500 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
              <i className="ri-function-line text-white text-xs"></i>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] text-slate-400 font-semibold uppercase mb-0.5">Result</div>
              <MathRenderer content={primaryContent} className="text-sm font-mono text-slate-800" />
            </div>
          </div>

          {/* Overview */}
          <div className={`bg-gradient-to-br ${modeOverviewBg} border rounded-xl p-4 transition-all duration-300`}>
            <div className="flex items-center gap-2 mb-2">
              <i className={`${modeOverviewIcon} text-sm`}></i>
              <span className={`text-xs font-bold ${modeOverviewText} uppercase tracking-wide`}>
                {mode === 'visual' ? 'The geometric picture' : mode === 'numerical' ? 'In concrete numbers' : mode === 'realworld' ? 'How it works in real life' : "What's happening here"}
              </span>
            </div>
            <p className="text-sm text-slate-700 leading-relaxed">{explanation.overview}</p>
          </div>

          {/* Steps */}
          <div className="space-y-3">
            <div className="text-xs font-bold text-slate-400 uppercase tracking-wide">
              {mode === 'visual' ? 'Visual Walkthrough' : mode === 'numerical' ? 'Number-by-Number' : mode === 'realworld' ? 'Real Applications' : 'Step-by-Step Breakdown'}
            </div>
            {explanation.steps.map((step, i) => {
              const c = colorMap[step.color] ?? colorMap.violet;
              return (
                <div
                  key={`${animKey}-${i}`}
                  className={`flex gap-4 transition-all duration-500 ${i < visibleSteps ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
                >
                  <div className="flex flex-col items-center flex-shrink-0">
                    <div className={`w-9 h-9 rounded-xl ${c.bg} flex items-center justify-center`}>
                      <i className={`${step.icon} ${c.icon} text-base`}></i>
                    </div>
                    {i < explanation.steps.length - 1 && (
                      <div className="w-px flex-1 mt-1 bg-slate-200 min-h-[12px]"></div>
                    )}
                  </div>
                  <div className="flex-1 pb-3">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded text-white ${c.badge}`}>{i + 1}</span>
                      <span className="text-sm font-bold text-slate-800">{step.title}</span>
                    </div>
                    <p className="text-sm text-slate-600 leading-relaxed">{step.content}</p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Tip — appears after all steps animate in */}
          {visibleSteps >= explanation.steps.length && (
            <div className={`${modeTipBg} border rounded-xl p-4 flex gap-3 animate-[fadeIn_0.4s_ease-out]`}>
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${modeTipIcon.split(' ')[0]}`}>
                <i className={`${modeTipIcon.split(' ').slice(1).join(' ')} text-base`}></i>
              </div>
              <div>
                <div className={`text-xs font-bold ${modeTipLabel} mb-1`}>
                  {mode === 'visual' ? 'Visual Tip' : mode === 'numerical' ? 'Code It Up' : mode === 'realworld' ? 'Real Impact' : 'Pro Tip'}
                </div>
                <p className={`text-sm ${modeTipText} leading-relaxed`}>{explanation.tip}</p>
              </div>
            </div>
          )}
        </div>

        {/* ── Footer ── */}
        <div className="px-6 pb-5 pt-3 border-t border-slate-100 flex gap-3 flex-shrink-0">
          <button
            onClick={cycleMode}
            className="flex items-center justify-center gap-2 flex-1 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl transition-all cursor-pointer whitespace-nowrap text-sm"
          >
            <i className="ri-refresh-line"></i>
            Explain in a Different Way
            <span className="text-[10px] font-normal text-slate-400 ml-0.5">
              → {MODES[(MODES.findIndex(m => m.key === mode) + 1) % MODES.length].label}
            </span>
          </button>
          <button
            onClick={onClose}
            className={`flex items-center justify-center gap-2 flex-1 py-3 bg-gradient-to-r ${modeHeaderGradient} hover:opacity-90 text-white font-bold rounded-xl transition-all shadow-md cursor-pointer whitespace-nowrap text-sm`}
          >
            Got it!
          </button>
        </div>
      </div>
    </div>
  );
}
